/**
 * Map step.
 *
 * Needs the campaign's own tactical-map assignment plus the shared SANDBOX
 * world hierarchy (city through sub-location) and the tactical map template
 * catalogue. The world-hierarchy reads compose `WorldMapEditorService`'s own
 * API clients rather than duplicating its CRUD idiom.
 */
export function createMapMethods({api, worldApi, subLocationsApi, positionsApi, connectionsApi, templatesApi}) {
    return {
        /**
         * @param {string} campaignId
         * @param {{position_id: string, template_id?: string|null}} assignment
         */
        async assignTacticalMap(campaignId, assignment) {
            const {data} = await api.gamemasterCampaignsTacticalMapCreate(campaignId, assignment);
            return data;
        },

        // ==================== World hierarchy (SANDBOX, shared) ====================
        // City -> Area -> Location -> SubLocation -> Position. Creating any node here
        // writes shared world data every campaign sees — never campaign-scoped.

        async listCities() {
            const {data} = await worldApi.gamemasterWorldCitiesList();
            return data;
        },

        /** @param {{name: string, description?: string}} city */
        async createCity({name, description = ''} = {}) {
            const {data} = await worldApi.gamemasterWorldCitiesCreate({name, description});
            return data;
        },

        async listAreas() {
            const {data} = await worldApi.gamemasterWorldAreasList();
            return data;
        },

        /** @param {{name: string, description?: string, city: string, area?: number}} area - `area` is the district's size, not a hierarchy link. */
        async createArea({name, description = '', city, area = 1} = {}) {
            const {data} = await worldApi.gamemasterWorldAreasCreate({name, description, city, area});
            return data;
        },

        async listLocations() {
            const {data} = await worldApi.gamemasterWorldLocationsList();
            return data;
        },

        /** @param {{name: string, description?: string, area: string}} location */
        async createLocation({name, description = '', area} = {}) {
            const {data} = await worldApi.gamemasterWorldLocationsCreate({name, description, area});
            return data;
        },

        async listSubLocations(locationId) {
            const {data} = await subLocationsApi.gamemasterWorldSubLocationsList(undefined, locationId);
            return data;
        },

        /**
         * Every sub-location in the world, unfiltered — what the world map needs
         * to pin a city's children without one request per location.
         */
        async listAllSubLocations() {
            const {data} = await subLocationsApi.gamemasterWorldSubLocationsList();
            return data;
        },

        /**
         * Move a city's pin on the painted world map.
         * @param {string} cityId
         * @param {{map_x: number, map_y: number}} anchor - percentages, 0-100.
         */
        async setCityAnchor(cityId, {map_x, map_y}) {
            const {data} = await worldApi.gamemasterWorldCitiesPartialUpdate(cityId, {map_x, map_y});
            return data;
        },

        /**
         * Move a sub-location's pin. Null on either axis returns it to the ring
         * its parent city lays out for unplaced children.
         * @param {string} subLocationId
         * @param {{map_x: number|null, map_y: number|null}} anchor
         */
        async setSubLocationAnchor(subLocationId, {map_x, map_y}) {
            const {data} = await subLocationsApi.gamemasterWorldSubLocationsPartialUpdate(subLocationId, {
                map_x,
                map_y,
            });
            return data;
        },

        /** @param {{name: string, description?: string, location: string}} subLocation */
        async createSubLocation({name, description = '', location} = {}) {
            const {data} = await subLocationsApi.gamemasterWorldSubLocationsCreate({name, description, location});
            return data;
        },

        // ==================== Positions + connections (world-graph rendering) ====================
        // Reads compose WorldMapEditorService's own API clients rather than
        // duplicating its CRUD; writes for the campaign's own positions still go
        // through `createPosition` in builder.js, which is campaign-builder-scoped.

        async listPositions(subLocationId) {
            const {data} = await positionsApi.gamemasterWorldPositionsList(undefined, undefined, undefined, subLocationId);
            return data;
        },

        async listPositionConnections() {
            const {data} = await connectionsApi.gamemasterWorldPositionConnectionsList();
            return data;
        },

        // ==================== Tactical map templates ====================

        async listTacticalMapTemplates() {
            const {data} = await templatesApi.tacticalmapTemplatesList();
            return data;
        },
    };
}
